import { Component, Suspense, useMemo, useEffect, useRef } from "react";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { gltfWithAuth } from "./gltfAuth";
import { useQuery } from "@tanstack/react-query";
import {
  fetchWarehouseTransformById,
  getWarehouseModelUrl,
  type WarehouseTransform,
} from "../api/rest";
import { patchMaterialForXray } from "./xray";

/** Envelope of the loaded GLB in its own authored metres — BEFORE the placement
 *  transform, so a consumer sizing itself from the model (ADR 310) does not
 *  re-fit while the operator drags the calibration sliders. */
export interface ModelBounds {
  center: [number, number, number];
  size: [number, number, number];
}

interface ModelProps {
  widthM: number;
  heightM: number;
  siteId: string;
  /** When set, fetches model + transform scoped to this warehouse. */
  warehouseId?: string;
  /** Override from calibration page — skips API fetch and uses this value directly */
  transformOverride?: WarehouseTransform | null;
  /** Called once per loaded model with its untransformed envelope (ADR 310). */
  onBounds?: (bounds: ModelBounds) => void;
}

function WarehouseGLB({ widthM, heightM, modelUrl, transform, onBounds }: {
  widthM: number;
  heightM: number;
  modelUrl: string;
  transform: WarehouseTransform | null;
  onBounds?: (bounds: ModelBounds) => void;
}) {
  const { scene } = useGLTF(modelUrl, true, true, gltfWithAuth);
  const warned = useRef(false);

  const clone = useMemo(() => {
    const c = scene.clone(true);
    c.traverse((obj) => {
      if ((obj as THREE.Mesh).isMesh) {
        const mesh = obj as THREE.Mesh;
        mesh.castShadow    = true;
        mesh.receiveShadow = true;
        // Follow-mode x-ray (ADR 103) — inert until the camera follows an asset.
        const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        mats.forEach(patchMaterialForXray);
      }
    });
    return c;
  }, [scene]);

  // An uploaded model rendering at identity (authored metres) is the correct
  // default, but it is still uncalibrated — say so once, and loudly enough to
  // find: the old console.debug for this was effectively invisible.
  useEffect(() => {
    if (transform) return;
    console.warn(
      `[WarehouseModel] ${modelUrl} has no saved calibration — rendering at identity ` +
      `(authored metres). Calibrate at /admin/warehouses/.../model if it lands wrong.`
    );
  }, [modelUrl, transform]);

  useEffect(() => {
    if (!onBounds) return;
    const box = new THREE.Box3().setFromObject(clone);
    const c = new THREE.Vector3(), s = new THREE.Vector3();
    box.getCenter(c);
    box.getSize(s);
    onBounds({ center: [c.x, c.y, c.z], size: [s.x, s.y, s.z] });
  }, [clone, onBounds]);

  useEffect(() => {
    if (warned.current || !transform) return;
    const box = new THREE.Box3().setFromObject(clone);
    const size = new THREE.Vector3();
    box.getSize(size);
    const devX = Math.abs(size.x - widthM) / widthM;
    const devZ = Math.abs(size.z - heightM) / heightM;
    if (devX > 0.2 || devZ > 0.2) {
      console.warn(
        `[WarehouseModel] GLB ${size.x.toFixed(1)}×${size.z.toFixed(1)}m vs zones ${widthM}×${heightM}m (>20%).`
      );
      warned.current = true;
    }
  }, [clone, widthM, heightM, transform]);

  if (transform) {
    const [ox, oy, oz] = transform.origin_offset;
    return (
      <group position={[ox, oy, oz]} rotation={[0, transform.rotation_y_rad, 0]} scale={transform.scale}>
        <primitive object={clone} />
      </group>
    );
  }
  return <primitive object={clone} />;
}

interface EBState { failed: boolean }
class WarehouseModelBoundary extends Component<ModelProps & { children: React.ReactNode }, EBState> {
  override state: EBState = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  override render() {
    if (this.state.failed) return null;
    return this.props.children;
  }
}

export function WarehouseModel({ widthM, heightM, siteId, warehouseId, transformOverride, onBounds }: ModelProps) {
  // Explicit-warehouse rule (retrospective C5): never invent `wh-{siteId}`.
  // Until the store resolves the real warehouse id (auto-select is one fetch),
  // BOTH queries below stay disabled — no model, no transform, a consistent
  // pair. The old "stale model + fresh transform" mismatch came from the
  // transform query using an invented id while this one was gated; gating
  // them identically fixes it.
  const { data: modelInfo } = useQuery({
    queryKey: ["warehouseModel", siteId, warehouseId],
    queryFn: () => getWarehouseModelUrl(siteId, warehouseId as string),
    staleTime: 5 * 60_000,
    enabled: !!warehouseId,
  });
  const modelUrl = modelInfo?.url;

  // When the resolved URL changes (e.g. after a re-upload bumps ?v=...), preload
  // the new GLB and evict the previous URL from drei's cache. Without the evict,
  // <primitive object={clone}> can keep showing the old scene because drei
  // returns the still-cached entry for the previous URL on stray renders.
  const prevUrlRef = useRef<string | null>(null);
  useEffect(() => {
    const url = modelInfo?.url;
    if (!url) return;
    if (prevUrlRef.current && prevUrlRef.current !== url) {
      useGLTF.clear(prevUrlRef.current);
    }
    useGLTF.preload(url, true, true, gltfWithAuth);
    prevUrlRef.current = url;
  }, [modelInfo?.url]);

  const { data: apiTransform } = useQuery({
    queryKey: ["warehouseTransform", warehouseId],
    queryFn: () => fetchWarehouseTransformById(siteId, warehouseId as string),
    staleTime: 60_000,
    enabled: transformOverride === undefined && !!warehouseId,
  });
  const transform = transformOverride !== undefined ? transformOverride : (apiTransform ?? null);

  // No uploaded model → render no building (bare floor); the rack/occupancy/zone layers
  // still render. The demo GLB fallback + its 60×40 auto-fit are gone (ADR 184).
  if (!modelUrl) return null;

  return (
    <WarehouseModelBoundary widthM={widthM} heightM={heightM} siteId={siteId} warehouseId={warehouseId}>
      <Suspense fallback={null}>
        {/* key={modelUrl} forces a full remount when the URL changes so the old
            <primitive object={oldClone}> is unambiguously removed from the scene
            graph before the new clone mounts. */}
        <WarehouseGLB key={modelUrl} widthM={widthM} heightM={heightM} modelUrl={modelUrl} transform={transform} onBounds={onBounds} />
      </Suspense>
    </WarehouseModelBoundary>
  );
}
